<script setup>
/* eslint-disable @intlify/vue-i18n/no-dynamic-keys */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WorkflowCanvasCard from './WorkflowCanvasCard.vue';
import WorkflowFlowConnector from './WorkflowFlowConnector.vue';
import {
  buildWorkflowAddStepMenuItems,
  getWorkflowActionNodeTitle,
  getWorkflowActionStepDescriptionI18nKeys,
  getWorkflowActionStepVisual,
  getWorkflowTriggerStepVisual,
} from './workflowCanvasStepTheme';
const props = defineProps({
  automation: { type: Object, required: true },
  automationActionTypes: { type: Array, required: true },
  automationRuleEvents: { type: Array, required: true },
});

const emit = defineEmits(['insertActionAfter']);

const activePanel = defineModel('activePanel', {
  type: String,
  required: true,
});
const activeAddMenuId = ref(null);

const { t } = useI18n();

const currentEventLabel = computed(() => {
  const ev = props.automationRuleEvents.find(
    e => e.key === props.automation.event_name
  );
  return ev?.value || props.automation.event_name;
});

const stepVisuals = computed(() =>
  (props.automation.actions || []).map(a =>
    getWorkflowActionStepVisual(a.action_name)
  )
);

const visualAt = i => stepVisuals.value[i];

const triggerVisual = computed(() => getWorkflowTriggerStepVisual());

const addStepMenuItems = computed(() => buildWorkflowAddStepMenuItems(t));

const stepDescriptionI18n = computed(() =>
  (props.automation.actions || []).map(a =>
    getWorkflowActionStepDescriptionI18nKeys(a.action_name)
  )
);

const actionShortDescriptions = computed(() =>
  stepDescriptionI18n.value.map(keys => t(keys.short))
);

const actionSummary = index => {
  const action = props.automation.actions[index];
  const type = props.automationActionTypes.find(
    a => a.key === action.action_name
  );
  return type?.label || action.action_name;
};

const actionNodeTitle = index => {
  const action = props.automation.actions[index];
  return getWorkflowActionNodeTitle(
    action.action_name,
    props.automation.actions,
    index,
    t
  );
};

const triggerActive = computed(
  () => activePanel.value === 'trigger-conditions'
);
const actionActive = i => activePanel.value === `action-${i}`;

const select = panel => {
  activePanel.value = panel;
};

const onAddStep = (afterIndex, actionName) => {
  emit('insertActionAfter', afterIndex, actionName);
};
</script>

<template>
  <div class="flex w-[210px] shrink-0 flex-col overflow-visible">
    <!-- Trigger node -->
    <div class="relative">
      <WorkflowCanvasCard
        :title="$t('AUTOMATION.FLOW.NODE_TRIGGER')"
        :description="currentEventLabel"
        :icon="triggerVisual.hugeIcon"
        :icon-class="triggerVisual.iconClass"
        :is-active="triggerActive"
        :active-card-class="triggerVisual.cardBorderClass"
        :active-ring-class="triggerVisual.ringActiveClass"
        @click="select('trigger-conditions')"
      />
    </div>

    <WorkflowFlowConnector
      v-model:active-add-menu-id="activeAddMenuId"
      connector-id="after-trigger"
      :line-class="triggerVisual.lineClass"
      :plus-button-class="triggerVisual.plusButtonClass"
      :items="addStepMenuItems"
      @select="actionName => onAddStep(-1, actionName)"
    />

    <!-- Action nodes -->
    <template
      v-for="(_action, i) in automation.actions"
      :key="`flow-action-${i}`"
    >
      <div class="relative">
        <WorkflowCanvasCard
          :title="actionNodeTitle(i)"
          :description="actionSummary(i)"
          :sub-description="actionShortDescriptions[i]"
          :icon="visualAt(i).hugeIcon"
          :icon-class="visualAt(i).iconClass"
          :is-active="actionActive(i)"
          :active-card-class="visualAt(i).cardBorderClass"
          :active-ring-class="visualAt(i).ringActiveClass"
          @click="select(`action-${i}`)"
        />
      </div>

      <WorkflowFlowConnector
        v-model:active-add-menu-id="activeAddMenuId"
        :connector-id="`after-action-${i}`"
        :line-class="visualAt(i).lineClass"
        :plus-button-class="visualAt(i).plusButtonClass"
        :items="addStepMenuItems"
        @select="actionName => onAddStep(i, actionName)"
      />
    </template>
  </div>
</template>
